import type { PhaseLaneDatum, PhaseLaneTick } from "@/components/visual/charts";
import type { VisualTable } from "@/components/visual/visual-table";
import { WorkCalendar } from "@/engine/calendar";
import { addDays, toDay } from "@/engine/date";
import { formatDate } from "@/lib/date-format";
import type { EvmSummary } from "@/engine/health";
import { fmtDelta } from "@/lib/format";
import type { Baseline, ISODate, Project, Schedule, ScheduledTask, Task } from "@/engine/types";

/**
 * 예측 보기 계산. 화면은 여기서 만든 숫자만 그린다.
 * 남은 일은 그 날까지 끝나지 않은 작업의 남은 기간(일)이다. 진척은 기준일 한 시점만 알 수 있으므로
 * 과거로 실적 곡선을 만들지 않는다.
 */

const MAX_BANDS = 6;
const MAX_SAMPLES = 36;

export interface RemainingSample {
  date: ISODate;
  /** 차트 눈금. 좁은 축용 short */
  label: string;
}

export interface RemainingBand {
  key: string;
  legend: string;
  /** samples와 같은 길이. 남은 일수 */
  values: number[];
}

export interface RemainingForecast {
  samples: RemainingSample[];
  /** 아래 띠가 배열의 앞 */
  bands: RemainingBand[];
  /** 합계 알약을 달 샘플 인덱스. 첫 날과 마지막 날을 포함한다 */
  markers: number[];
}

export function fmtWorkdays(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return new Intl.NumberFormat("ko-KR", { maximumFractionDigits: 1 }).format(rounded);
}

function unfinishedDays(task: ScheduledTask): number {
  if (task.isSummary || task.isMilestone || task.duration <= 0 || task.percentComplete >= 100) return 0;
  const pct = Math.min(100, Math.max(0, task.percentComplete));
  return task.duration * (1 - pct / 100);
}

function phaseOf(id: string, byId: Map<string, Task>): Task | null {
  let current = byId.get(id);
  const seen = new Set<string>();
  while (current?.parentId && byId.has(current.parentId) && !seen.has(current.id)) {
    seen.add(current.id);
    current = byId.get(current.parentId);
  }
  return current ?? null;
}

function sampleOrdinals(startOrd: number, endOrd: number): number[] {
  if (endOrd <= startOrd) return [startOrd];
  const span = endOrd - startOrd;
  const count = Math.min(MAX_SAMPLES, span + 1);
  const ords: number[] = [];
  for (let i = 0; i < count; i++) {
    const ord = Math.round(startOrd + (span * i) / (count - 1));
    if (ords[ords.length - 1] !== ord) ords.push(ord);
  }
  if (ords[ords.length - 1] !== endOrd) ords.push(endOrd);
  return ords;
}

function markerIndexes(count: number): number[] {
  if (count <= 1) return [0];
  const want = Math.min(4, count);
  const picked = new Set<number>([0, count - 1]);
  for (let k = 1; k < want - 1; k++) picked.add(Math.round((k * (count - 1)) / (want - 1)));
  return [...picked].sort((a, b) => a - b);
}

export function remainingForecast(project: Project, schedule: Schedule, calendar: WorkCalendar = new WorkCalendar(project.calendar)): RemainingForecast {
  const byId = new Map(project.tasks.map((task) => [task.id, task]));
  const statusOrd = calendar.ordinalOfISO(project.statusDate);
  const endOrd = calendar.ordinalBeforeISO(schedule.projectFinish);
  const ords = sampleOrdinals(statusOrd, Math.max(statusOrd, endOrd));
  const phases: Task[] = [];
  const seenPhase = new Set<string>();
  for (const id of schedule.order) {
    const phase = phaseOf(id, byId);
    if (!phase || seenPhase.has(phase.id)) continue;
    seenPhase.add(phase.id);
    phases.push(phase);
  }

  const leaves: Array<{ phaseId: string; finishOrd: number; days: number }> = [];
  for (const id of schedule.order) {
    const task = schedule.tasks.get(id);
    if (!task) continue;
    const days = unfinishedDays(task);
    if (days <= 0) continue;
    const phase = phaseOf(id, byId);
    if (!phase) continue;
    leaves.push({ phaseId: phase.id, finishOrd: calendar.ordinalBeforeISO(task.finish), days });
  }

  const values = new Map<string, number[]>(phases.map((phase) => [phase.id, ords.map(() => 0)]));
  ords.forEach((ord, index) => {
    for (const leaf of leaves) {
      const stillOpen = leaf.finishOrd >= ord;
      const overdueNow = index === 0 && leaf.finishOrd < statusOrd;
      if (!stillOpen && !overdueNow) continue;
      const row = values.get(leaf.phaseId);
      if (row) row[index] = (row[index] ?? 0) + leaf.days;
    }
  });

  const ranked = phases
    .map((phase) => ({ phase, total: (values.get(phase.id) ?? []).reduce((sum, n) => sum + n, 0) }))
    .filter((row) => row.total > 0);
  const kept = ranked.length > MAX_BANDS ? ranked.slice(0, MAX_BANDS - 1) : ranked;
  const rest = ranked.length > MAX_BANDS ? ranked.slice(MAX_BANDS - 1) : [];
  const bands: RemainingBand[] = kept.map(({ phase }) => ({
    key: phase.id,
    legend: phase.name || "이름 없음",
    values: values.get(phase.id) ?? ords.map(() => 0),
  }));
  if (rest.length > 0) {
    const merged = ords.map((_, index) => rest.reduce((sum, row) => sum + (values.get(row.phase.id)?.[index] ?? 0), 0));
    bands.push({ key: "rest", legend: "그 밖", values: merged });
  }

  return {
    samples: ords.map((ord) => {
      const date = calendar.isoOf(ord);
      return { date, label: formatDate(date, "short") };
    }),
    bands,
    markers: bands.length === 0 ? [] : markerIndexes(ords.length),
  };
}

export function remainingTotal(forecast: RemainingForecast, index: number): number {
  return forecast.bands.reduce((sum, band) => sum + (band.values[index] ?? 0), 0);
}

/** 기준일 판정. 가장 큰 단계 둘만 이름을 붙인다. */
export function remainingSummary(forecast: RemainingForecast): string {
  const now = remainingTotal(forecast, 0);
  const ranked = forecast.bands
    .map((band, index) => ({ name: band.legend, days: band.values[0] ?? 0, index }))
    .filter((band) => band.days > 0)
    .sort((a, b) => b.days - a.days || a.index - b.index)
    .slice(0, 2);
  const head = `지금 ${fmtWorkdays(now)}일 남았습니다.`;
  if (ranked.length === 0) return head;
  return `${head} ${ranked.map((band) => `${band.name} ${fmtWorkdays(band.days)}일`).join(", ")}입니다.`;
}

/** 받침이 있으면 은, 없으면 는. 목록이면 마지막 글자를 본다. */
function topic(name: string): string {
  const last = name.charCodeAt(name.length - 1);
  const batchim = last >= 0xac00 && last <= 0xd7a3 && (last - 0xac00) % 28 !== 0;
  return `${name}${batchim ? "은" : "는"}`;
}

export interface PhaseLaneModel {
  lanes: PhaseLaneDatum[];
  referenceAt: number | null;
  ticks: PhaseLaneTick[];
  headline: string;
  ariaLabel: string;
  table: VisualTable;
}

function place(date: ISODate, start: ISODate, end: ISODate): number {
  const span = Math.max(1, toDay(end) - toDay(start));
  return Math.min(1, Math.max(0, (toDay(date) - toDay(start)) / span));
}

/**
 * 남은 작업과 같은 입력으로, 단계가 비는 날을 레인으로 둔다.
 * 기준선이 기준일보다 앞이면 선은 그리지 않고, 레인은 모두 기준선 뒤다.
 */
export function phaseLanes(forecast: RemainingForecast, reference: ISODate | null, onPick?: (date: ISODate) => void): PhaseLaneModel {
  const domainStart = forecast.samples[0]?.date ?? "";
  const draft = forecast.bands.flatMap((band) => {
    let last = -1;
    band.values.forEach((value, index) => {
      if (value > 0) last = index;
    });
    if (last < 0 || !domainStart) return [];
    const end = forecast.samples[last]?.date ?? domainStart;
    return [{ key: band.key, label: band.legend, start: domainStart, end, remaining: band.values[0] ?? 0 }];
  });
  let domainEnd = forecast.samples.at(-1)?.date ?? domainStart;
  for (const lane of draft) if (lane.end > domainEnd) domainEnd = lane.end;
  const passed = reference !== null && domainStart !== "" && reference < domainStart;
  if (reference && !passed && reference > domainEnd) domainEnd = reference;
  if (domainEnd <= domainStart && domainStart) domainEnd = addDays(domainStart, 1);

  const lanes: PhaseLaneDatum[] = draft.map((lane) => {
    let lateFrom: number | null = null;
    if (reference) {
      if (passed || reference <= lane.start) lateFrom = 0;
      else if (reference < lane.end) {
        const span = toDay(lane.end) - toDay(lane.start);
        lateFrom = span <= 0 ? 0 : (toDay(reference) - toDay(lane.start)) / span;
      }
    }
    const endLabel = formatDate(lane.end, "full");
    return {
      key: lane.key,
      label: lane.label,
      detail: `남은 ${fmtWorkdays(lane.remaining)}일 · ${endLabel}에 빔`,
      start: place(lane.start, domainStart, domainEnd),
      end: place(lane.end, domainStart, domainEnd),
      lateFrom,
      onClick: onPick ? () => onPick(lane.end) : undefined,
    };
  });

  const referenceAt = reference && !passed && reference >= domainStart && reference <= domainEnd ? place(reference, domainStart, domainEnd) : null;
  const ticks: PhaseLaneTick[] = [
    { at: 0, label: domainStart ? formatDate(domainStart, "short") : "", align: "start" },
    { at: 1, label: domainEnd ? formatDate(domainEnd, "short") : "", align: "end" },
  ];
  const latest = draft.reduce<string | null>((end, lane) => (end === null || lane.end > end ? lane.end : end), null);
  const latestNames = latest ? draft.filter((lane) => lane.end === latest).map((lane) => lane.label) : [];
  const lateNames = reference ? draft.filter((lane) => lane.end > reference).map((lane) => lane.label) : [];
  let headline = "";
  if (draft.length === 0) headline = "";
  else if (!reference) headline = `${topic(latestNames.join(", "))} ${formatDate(latest ?? domainEnd, "full")}에 비습니다.`;
  else if (passed) headline = `기준선 ${formatDate(reference, "full")}은 기준일보다 앞입니다.`;
  else if (lateNames.length === 0) headline = `모든 단계가 기준선 ${formatDate(reference, "full")} 전에 비습니다.`;
  else headline = `${topic(lateNames.join(", "))} 기준선 ${formatDate(reference, "full")} 뒤에 끝납니다.`;

  const table: VisualTable = {
    ariaLabel: "단계가 비는 날",
    columns: [
      { key: "name", label: "단계" },
      { key: "left", label: "남은 일", numeric: true },
      { key: "end", label: "비는 날", numeric: true },
      { key: "vs", label: "기준선" },
    ],
    rows: draft.map((lane) => ({
      key: lane.key,
      cells: [lane.label, fmtWorkdays(lane.remaining), formatDate(lane.end, "numeric"), reference === null ? "—" : lane.end > reference ? "뒤" : "앞"],
      onActivate: onPick ? () => onPick(lane.end) : undefined,
      activateLabel: onPick ? "일정에서 이 날 보기" : undefined,
    })),
  };

  return { lanes, referenceAt, ticks, headline, ariaLabel: headline || "남은 단계가 없습니다", table };
}

export function remainingTable(forecast: RemainingForecast, onPick?: (date: ISODate) => void): VisualTable {
  return {
    ariaLabel: "남은 작업 표",
    columns: [
      { key: "date", label: "날짜", numeric: true },
      ...forecast.bands.map((band) => ({ key: band.key, label: band.legend, numeric: true })),
      { key: "total", label: "합계", numeric: true },
    ],
    rows: forecast.samples.map((sample, index) => ({
      key: sample.date,
      cells: [formatDate(sample.date, "numeric"), ...forecast.bands.map((band) => fmtWorkdays(band.values[index] ?? 0)), fmtWorkdays(remainingTotal(forecast, index))],
      onActivate: onPick ? () => onPick(sample.date) : undefined,
      activateLabel: onPick ? "일정에서 이 날 보기" : undefined,
    })),
  };
}

/* ---------------------------------------------------------------- 계획 누적 */

export interface PlanCurve {
  dates: ISODate[];
  labels: string[];
  titles: string[];
  /** 계획 누적 0..100. labels와 같은 길이 */
  planned: number[];
  /** 실적은 기준일에 해당하는 한 점. 나머지 칸은 null */
  earned: Array<number | null>;
  /** 착수일(0%)부터 기준일(실적%)까지 이어지는 실적 누적 곡선 */
  actualLine: Array<number | null>;
  /** 기준일(실적%)부터 현재 잔여 종료일까지 이어지는 예측 추세선 */
  projectedLine: Array<number | null>;
  statusIndex: number;
}

/** 눈금이 겹치지 않게, 서로 떨어진 시점만 글자를 남긴다. 처음·기준일·마지막은 우선한다. */
function labelIndexes(count: number, statusIndex: number): Set<number> {
  if (count <= 6) return new Set(Array.from({ length: count }, (_, i) => i));
  const gap = Math.max(2, Math.ceil(count / 7));
  const want = [0, statusIndex, count - 1];
  for (let k = 1; k <= 2; k++) want.push(Math.round((k * (count - 1)) / 3));
  const sorted = [...new Set(want)].sort((a, b) => a - b);
  const kept: number[] = [];
  const locked = (index: number) => index === 0 || index === statusIndex || index === count - 1;
  for (const index of sorted) {
    const prev = kept[kept.length - 1];
    if (prev === undefined || index - prev >= gap) {
      kept.push(index);
      continue;
    }
    if (locked(index) && !locked(prev)) kept[kept.length - 1] = index;
  }
  return new Set(kept);
}

/** 기준일 실적과 같은 날 계획을 한 문장으로. */
export function planSummary(curve: PlanCurve): string {
  const earned = curve.earned[curve.statusIndex] ?? 0;
  const planned = curve.planned[curve.statusIndex] ?? 0;
  return `기준일 실적 ${Math.round(earned)}%. 같은 날 계획은 ${Math.round(planned)}%입니다.`;
}

/** 기준선이 없으면 null. 실적 곡선은 기준일 한 점만 둔다. */
export function planCurve(evm: EvmSummary): PlanCurve | null {
  if (!evm.hasBaseline || evm.curve.length < 2 || evm.ev === null) return null;
  let statusIndex = 0;
  let best = Infinity;
  evm.curve.forEach((point, index) => {
    const dist = Math.abs(point.day - evm.statusDay);
    if (dist < best) {
      best = dist;
      statusIndex = index;
    }
  });
  const show = labelIndexes(evm.curve.length, statusIndex);
  const earned = evm.ev * 100;
  const planned = evm.curve.map((point) => Math.round(point.pv * 1000) / 10);
  const pvAtStatus = planned[statusIndex] ?? 0;
  const actualLine = evm.curve.map((_, index) => {
    if (index > statusIndex) return null;
    if (index === statusIndex) return Math.round(earned * 10) / 10;
    if (pvAtStatus > 0) return Math.round((planned[index]! / pvAtStatus) * earned * 10) / 10;
    return statusIndex > 0 ? Math.round((index / statusIndex) * earned * 10) / 10 : Math.round(earned * 10) / 10;
  });
  const lastIndex = evm.curve.length - 1;
  const remSpan = Math.max(1, lastIndex - statusIndex);
  const projectedLine = evm.curve.map((_, index) => {
    if (index < statusIndex) return null;
    if (index === statusIndex) return Math.round(earned * 10) / 10;
    const t = (index - statusIndex) / remSpan;
    return Math.min(100, Math.round((earned + (100 - earned) * t) * 10) / 10);
  });
  return {
    dates: evm.curve.map((point) => point.date),
    labels: evm.curve.map((point, index) => (show.has(index) ? formatDate(point.date, "short") : "")),
    titles: evm.curve.map((point) => formatDate(point.date, "numeric")),
    planned,
    earned: evm.curve.map((_, index) => (index === statusIndex ? Math.round(earned * 10) / 10 : null)),
    actualLine,
    projectedLine,
    statusIndex,
  };
}

export function planTable(curve: PlanCurve, onPick?: (date: ISODate) => void): VisualTable {
  return {
    ariaLabel: "계획 누적 표",
    columns: [
      { key: "date", label: "날짜", numeric: true },
      { key: "plan", label: "계획", numeric: true },
      { key: "earned", label: "실적", numeric: true },
    ],
    rows: curve.dates.map((date, index) => ({
      key: date,
      cells: [curve.titles[index] ?? formatDate(date, "numeric"), `${Math.round(curve.planned[index] ?? 0)}%`, curve.earned[index] === null ? "—" : `${Math.round(curve.earned[index]!)}%`],
      onActivate: onPick ? () => onPick(date) : undefined,
      activateLabel: onPick ? "일정에서 이 날 보기" : undefined,
    })),
  };
}

/* ---------------------------------------------------------------- 종료일을 움직이는 작업 */

export interface FinishDriver {
  taskId: string;
  wbs: string;
  name: string;
  percent: number;
  totalFloat: number;
  finish: ISODate;
  /** 기준선이 없으면 null */
  varianceDays: number | null;
}

export function finishDrivers(project: Project, schedule: Schedule, baseline: Baseline | null, calendar: WorkCalendar = new WorkCalendar(project.calendar)): FinishDriver[] {
  const byId = new Map(project.tasks.map((task) => [task.id, task]));
  const drivers: FinishDriver[] = [];
  for (const id of schedule.criticalPath) {
    const task = schedule.tasks.get(id);
    if (!task || task.isSummary || task.percentComplete >= 100) continue;
    const base = baseline?.tasks[id];
    drivers.push({
      taskId: id,
      wbs: task.wbs,
      name: byId.get(id)?.name || "이름 없음",
      percent: task.percentComplete,
      totalFloat: task.totalFloat,
      finish: task.finish,
      varianceDays: base ? calendar.ordinalBeforeISO(task.finish) - calendar.ordinalBeforeISO(base.finish) : null,
    });
  }
  return drivers;
}

export function driverTable(drivers: readonly FinishDriver[], onOpen?: (taskId: string) => void): VisualTable {
  const hasBaseline = drivers.some((row) => row.varianceDays !== null);
  return {
    ariaLabel: "종료일을 움직이는 작업",
    columns: [
      { key: "wbs", label: "WBS" },
      { key: "name", label: "작업" },
      { key: "percent", label: "진척", numeric: true },
      { key: "float", label: "여유", numeric: true },
      { key: "finish", label: "종료", numeric: true },
      ...(hasBaseline ? [{ key: "variance", label: "계획 대비", numeric: true }] : []),
    ],
    rows: drivers.map((row) => ({
      key: row.taskId,
      cells: [`${row.wbs}`, row.name, `${Math.round(row.percent)}%`, row.totalFloat, formatDate(row.finish, "numeric"), ...(hasBaseline ? [row.varianceDays === null ? "—" : fmtDelta(row.varianceDays)] : [])],
      onActivate: onOpen ? () => onOpen(row.taskId) : undefined,
      activateLabel: onOpen ? "일정에서 이 작업 보기" : undefined,
    })),
  };
}
